import { Type } from "@sinclair/typebox";
import { IdSchema, JsonValueSchema, Sha256Schema, TimestampSchema } from "./shared.js";
import { ReportRenderParametersSchema } from "./report.js";
import { CodexStateBindingSchema } from "./codex-state.js";
export const RuntimeTargetSchema = Type.Union([
    Type.Object({
        kind: Type.Literal("host"),
        brokerId: IdSchema,
        instanceName: IdSchema,
        overlayId: IdSchema,
        imageDigest: Sha256Schema,
    }, { additionalProperties: false }),
    Type.Object({
        kind: Type.Literal("kubernetes"),
        namespace: IdSchema,
        podUid: IdSchema,
        pvcUid: IdSchema,
        imageDigest: Sha256Schema,
        fencingToken: IdSchema,
    }, { additionalProperties: false }),
]);
const RuntimeRequestBaseSchema = {
    requestId: IdSchema,
    identityId: IdSchema,
    sessionId: IdSchema,
    generation: Type.Integer({ minimum: 0 }),
    protocolDigest: Sha256Schema,
    target: RuntimeTargetSchema,
    targetDigest: Sha256Schema,
    deadline: TimestampSchema,
    requestDigest: Sha256Schema,
    accessToken: Type.String({ minLength: 32, maxLength: 2048 }),
};
const EmptyRuntimeParametersSchema = Type.Object({}, { additionalProperties: false });
export const PreparationLeaseSchema = Type.Object({
    ownerId: IdSchema,
    fence: Type.String({ pattern: "^[1-9][0-9]*$" }),
    actionGrantId: IdSchema,
    actionRequestDigest: Sha256Schema,
    grantDigest: Sha256Schema,
}, { additionalProperties: false });
export const RuntimePreparationObservationSchema = Type.Object({ running: Type.Boolean(), executionActive: Type.Boolean() }, { additionalProperties: false });
const RuntimeScopeParametersSchema = Type.Object({ scope: Type.Union([Type.Literal("tools"), Type.Literal("session")]) }, { additionalProperties: false });
function runtimeRequestVariant(operation, parameters) {
    return Type.Object({
        ...RuntimeRequestBaseSchema,
        operation: Type.Literal(operation),
        parameters,
    }, { additionalProperties: false });
}
export const RuntimeRequestSchema = Type.Union([
    runtimeRequestVariant("create", Type.Object({ preparationLease: Type.Optional(PreparationLeaseSchema) }, { additionalProperties: false })),
    runtimeRequestVariant("inspect", EmptyRuntimeParametersSchema),
    runtimeRequestVariant("report_render", ReportRenderParametersSchema),
    runtimeRequestVariant("rotate", Type.Object({
        previousGeneration: Type.Integer({ minimum: 0 }),
        previousTargetDigest: Sha256Schema,
    }, { additionalProperties: false })),
    runtimeRequestVariant("start", Type.Object({
        kind: Type.Literal("model_worker"),
        payload: Type.Object({
            sizeBytes: Type.Integer({ minimum: 1, maximum: 5 * 1024 ** 3 }),
            digest: Sha256Schema,
            maximumRequestBytes: Type.Integer({ minimum: 1, maximum: 5 * 1024 ** 3 }),
        }, { additionalProperties: false }),
        modelState: Type.Optional(CodexStateBindingSchema),
    }, { additionalProperties: false })),
    runtimeRequestVariant("stream_read", Type.Object({
        path: Type.String({ minLength: 1, maxLength: 4_096 }),
        offset: Type.String({ pattern: "^(0|[1-9][0-9]*)$" }),
        length: Type.Integer({ minimum: 1, maximum: 64 * 1_024 * 1_024 }),
        actionGrantId: IdSchema,
        actionRequestDigest: Sha256Schema,
        inputFile: Type.Union([
            Type.Object({
                manifestDigest: Sha256Schema,
                fileId: IdSchema,
                digest: Sha256Schema,
                sizeBytes: Type.Integer({ minimum: 0, maximum: 20 * 1024 ** 3 }),
            }, { additionalProperties: false }),
            Type.Null(),
        ]),
    }, { additionalProperties: false })),
    runtimeRequestVariant("stream_write", Type.Object({
        path: Type.String({ minLength: 1, maxLength: 4_096 }),
        maximumBytes: Type.Integer({ minimum: 1, maximum: 1_024 * 1_024 * 1_024 }),
    }, { additionalProperties: false })),
    runtimeRequestVariant("workspace_list", Type.Object({
        path: Type.String({ minLength: 1, maxLength: 4_096 }),
        maximumEntries: Type.Integer({ minimum: 1, maximum: 10_000 }),
    }, { additionalProperties: false })),
    runtimeRequestVariant("workspace_read", Type.Object({
        path: Type.String({ minLength: 1, maxLength: 4_096 }),
        offset: Type.String({ pattern: "^(0|[1-9][0-9]*)$" }),
        length: Type.Integer({ minimum: 1, maximum: 64 * 1_024 * 1_024 }),
    }, { additionalProperties: false })),
    runtimeRequestVariant("workspace_remove", Type.Object({ path: Type.String({ minLength: 1, maxLength: 4_096 }) }, { additionalProperties: false })),
    runtimeRequestVariant("cancel", EmptyRuntimeParametersSchema),
    runtimeRequestVariant("stop", RuntimeScopeParametersSchema),
    runtimeRequestVariant("confirm_absent", RuntimeScopeParametersSchema),
    runtimeRequestVariant("delete_storage", RuntimeScopeParametersSchema),
]);
export const RuntimeResultSchema = Type.Object({
    requestId: IdSchema,
    status: Type.Union([
        Type.Literal("succeeded"),
        Type.Literal("failed"),
        Type.Literal("cancelled"),
        Type.Literal("timed_out"),
    ]),
    targetDigest: Sha256Schema,
    result: JsonValueSchema,
}, { additionalProperties: false });
export const RuntimeProbeRequestSchema = Type.Object({
    requestId: IdSchema,
    brokerId: Sha256Schema,
    protocolDigest: Sha256Schema,
    deadline: TimestampSchema,
}, { additionalProperties: false });
export const RuntimeProbeResultSchema = Type.Object({
    requestId: IdSchema,
    status: Type.Literal("ready"),
    brokerId: Sha256Schema,
    protocolDigest: Sha256Schema,
}, { additionalProperties: false });
export const RuntimeMessageBodySchema = Type.Union([
    Type.Object({ kind: Type.Literal("request"), value: RuntimeRequestSchema }, { additionalProperties: false }),
    Type.Object({ kind: Type.Literal("result"), value: RuntimeResultSchema }, { additionalProperties: false }),
    Type.Object({ kind: Type.Literal("probe"), value: RuntimeProbeRequestSchema }, { additionalProperties: false }),
    Type.Object({ kind: Type.Literal("probe_result"), value: RuntimeProbeResultSchema }, { additionalProperties: false }),
]);
//# sourceMappingURL=runtime.js.map